import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import { fr as frDayPicker } from 'react-day-picker/locale/fr';
import { Box, styled, Popover, useTheme } from '@mui/material';
import { formatDate } from '../helpers/date';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/24/arrow_left.svg';
import { FilterArrow, FilterButtonCaption, FilterButtonLabel } from './FilterToolbarStyles';
import { getFilterButtonBorder, getThemeColors } from '../helpers/themeStyles';
import { THEME_MODES } from '../theme';
import 'react-day-picker/style.css';

const CustomChevron = ({ orientation, size = 24, className, disabled }) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const isRight = orientation === 'right';

  return (
    <Box
      component={ArrowLeftIcon}
      className={className}
      sx={{
        width: size,
        height: size,
        opacity: disabled ? 0.5 : 1,
        transform: isRight ? 'rotate(180deg)' : 'none',
        display: 'inline-block',
        flexShrink: 0,
        '& path': { fill: colors.textSecondary },
      }}
    />
  );
};

const DateRangeField = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$open',
})(({ theme, $open }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 20px',
  borderRadius: 40,
  border: getFilterButtonBorder(theme, $open),
  cursor: 'pointer',
  minWidth: 140,
  width: 240,
  height: 44,
  boxSizing: 'border-box',
  justifyContent: 'space-between',
  color: getThemeColors(theme).textPrimary,
}));

const DateRangePopoverContent = styled(Box)(({ theme }) => {
  const colors = getThemeColors(theme);
  const isDark = theme.palette.mode === THEME_MODES.DARK;

  return {
    padding: 0,
    overflow: 'hidden',
    '& table': {
      borderSpacing: 0,
    },
    '& .rdp-day': {
      border: 'none',
      padding: 0,
    },
    '& .rdp-selected': {
      '& .rdp-day_button': {
        color: colors.activeTextOnAccent,
      },
    },
    '& .rdp-outside:not(.rdp-range_start):not(.rdp-range_end):not(.rdp-range_middle)': {
      opacity: 0.3,
    },
    '& .rdp-root': {
      '--rdp-accent-color': isDark ? '#B9FF66' : theme.palette.primary.main,
      '--rdp-accent-background-color': isDark ? '#B9FF66' : theme.palette.primary.main,
      '--rdp-range_start-color': colors.activeTextOnAccent,
      '--rdp-range_end-color': colors.activeTextOnAccent,
      '--rdp-range_middle-color': colors.textPrimary,
      '--rdp-day_button-border': 'none',
      '--rdp-selected-border': 'none',
      '--rdp-day-height': '40px',
      '--rdp-day-width': '40px',
      color: colors.textPrimary,
    },
    '& .rdp-range_start:not(.rdp-range_end)': {
      '& .rdp-day_button': {
        borderTopLeftRadius: 20,
        borderBottomLeftRadius: 20,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    '& .rdp-range_end:not(.rdp-range_start)': {
      '& .rdp-day_button': {
        borderTopRightRadius: 20,
        borderBottomRightRadius: 20,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
    },
    '& .rdp-day_button': {
      color: colors.textPrimary,
    },
    '& .rdp-month_caption': {
      margin: 0,
      padding: '12px 16px',
      borderRadius: '6px 6px 0 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 44,
    },
    '& .rdp-caption_label': {
      color: colors.textPrimary,
      fontWeight: 700,
      fontSize: 16,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    '& .rdp-nav': {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    '& .rdp-button_previous, & .rdp-button_next': {
      color: colors.textSecondary,
    },
    '& .rdp-chevron': {
      fill: colors.textSecondary,
    },
    '& .rdp-weekday': {
      color: colors.textSecondary,
      textTransform: 'uppercase',
      fontWeight: 500,
      textAlign: 'center',
    },
    '& .rdp-months': {
      padding: '16px 24px 24px',
    },
  };
});

const DateRangePicker = ({ value, onChange, label = 'Date', placeholder = 'Toutes' }) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const range = value ?? { start: null, end: null };
  const from = range.start
    ? range.start instanceof Date
      ? range.start
      : new Date(range.start)
    : undefined;
  const to = range.end ? (range.end instanceof Date ? range.end : new Date(range.end)) : undefined;
  const selected = from || to ? { from, to } : undefined;

  const handleSelect = useCallback(
    (nextRange) => {
      if (!nextRange) return;
      const { from: nextFrom, to: nextTo } = nextRange;
      onChange({
        start: nextFrom ?? null,
        end: nextTo ?? null,
      });
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const displayText =
    range.start && range.end
      ? `${formatDate(range.start, { format: 'short' })} - ${formatDate(range.end, { format: 'short' })}`
      : range.start
        ? formatDate(range.start, { format: 'short' }) + ' - ...'
        : range.end
          ? '... - ' + formatDate(range.end, { format: 'short' })
          : placeholder;

  return (
    <>
      <DateRangeField
        $open={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
          <FilterButtonCaption>{label}</FilterButtonCaption>
          <FilterButtonLabel sx={{ fontSize: 12 }}>{displayText}</FilterButtonLabel>
        </Box>
        <FilterArrow open={open} />
      </DateRangeField>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              background: colors.menuBg,
              borderRadius: 6,
              border: `1px solid ${colors.borderSubtle}`,
              marginTop: 0.5,
            },
          },
        }}
      >
        <DateRangePopoverContent>
          <DayPicker
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={from || to || new Date()}
            showOutsideDays
            locale={frDayPicker}
            navLayout="around"
            components={{ Chevron: CustomChevron }}
            formatters={{
              formatWeekdayName: (weekday) =>
                format(weekday, 'EEE', { locale: fr }).replace(/\.$/, ''),
            }}
          />
        </DateRangePopoverContent>
      </Popover>
    </>
  );
};

export default DateRangePicker;
